import { useMemo } from 'react';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';
import { expVal } from '@confluence/feature-experiments';
import { usePageSpaceKey } from '@confluence/page-context';

import { useIsSpaceOrSiteAdmin } from './useIsSpaceOrSiteAdmin.ts';
import { useSPAViewContext } from './content-ownership-helpers.ts';

export const useIsAllowedToChangeOwner = (ownedByAccountId: string | null): boolean => {
	const { userId } = useSessionData();
	const isSpaceOrSiteAdmin = useIsSpaceOrSiteAdmin();
	const [spaceKey] = usePageSpaceKey();
	const { isSiteAdmin } = useSPAViewContext();
	const { canManageContent = false } =
		useSpaceManagementOperations({
			spaceKey: spaceKey ?? '',
			skip: !spaceKey || !expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false),
		}) ?? {};

	const isElevated = expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)
		? canManageContent || isSiteAdmin
		: isSpaceOrSiteAdmin;

	return useMemo(() => {
		if (!userId) {
			return false;
		}
		const isCurrentUserOwner = ownedByAccountId === userId;
		return isElevated || isCurrentUserOwner;
	}, [isElevated, ownedByAccountId, userId]);
};
